import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, getLocale, useIntl } from 'umi';
import { arrayRemoveElement } from '../../utils/common';
import { useInfiniteScroll } from 'ahooks';
import { buriedPointSearch } from '@/pages/home/utils/buriedPoint';
import { debounce } from 'lodash-es';
import { ReportProps } from './report';
import { DATABRAIN_COS_ACCELERATE, DATABRAIN_COS_DOMAIN } from '@intelligence/_config/config';
import { OrderTypeMap } from '@intelligence/pages/InsightsReport/components/SortBySelector';
import { sortReportsData } from '@intelligence/pages/InsightsReport/_utils';
import { isEmpty } from '@intelligence/v3/utils/common';
import { LANGUAGE_CN } from '@home/_config/config';
import { splitReportTagStr } from '@intelligence/v3/utils/topcharts';
import { getReportLanguages, normalizeLanguage, collectLanguageTabs, LANG_TAB_I18N_MAP } from '@intelligence/utils/reportLanguage';
import { buildReportAuthors } from '@intelligence/components/ReportCard/authors';

interface Result {
  list: [];
  nextId: number | undefined;
}
const transformAuthorAvatar = (avatar: string): string => avatar.replace(DATABRAIN_COS_DOMAIN, DATABRAIN_COS_ACCELERATE);

export default (props: ReportProps) => {
  const { segmentList, getReportFun, searchType, pageLogMes, reportType } = props;

  const { formatMessage } = useIntl();

  const [dataLoading, setLoading] = useState(true);
  const locale = getLocale();
  const [dataFull, setDataFull] = useState([]);
  const [activeId, setActiveId] = useState('all');
  const [dataPartial, setDataPartial] = useState([]);
  const [dataShow, setDataShow] = useState([]);
  const [cacheRecentSearch, setCacheRecentSearch] = useState([]);
  // 排序选项
  const [orderType, setOrderType] = useState<string>(OrderTypeMap.time);

  const languageSegmentList = useMemo(() => {
    const allTab = [{ text: formatMessage({ id: 'dbi_reports_all' }), value: 'all' }];
    if (isEmpty(dataFull)) return allTab;
    return [...allTab, ...collectLanguageTabs(dataFull, 'filePosition', (lang) => {
      const key = LANG_TAB_I18N_MAP[lang];
      return key ? formatMessage({ id: key }) : `${lang} Report`;
    })];
  }, [dataFull, locale]);

  const [segmentValue, setSegmentValue] = useState(languageSegmentList?.[0]?.value || '');

  const handleSegmentChange = (payload: string) => {
    setSegmentValue(payload);
  };

  useEffect(() => {
    const activeIdLowerCase = activeId?.toLowerCase();

    const dataPartialNew = dataFull?.filter((item: any) => {
      if (activeIdLowerCase !== 'all' && item?.source?.toLowerCase() !== activeIdLowerCase) return false;

      if (segmentValue === 'all') {
        return item?.pdf !== '' || item?.pdf_en !== '';
      }
      const { fileCnLang, fileEnLang } = getReportLanguages(item);
      const normalizedSegment = normalizeLanguage(segmentValue);
      return (
        (normalizeLanguage(fileCnLang) === normalizedSegment && item?.pdf !== '') ||
        (normalizeLanguage(fileEnLang) === normalizedSegment && item?.pdf_en !== '')
      );
    });

    setDataPartial(dataPartialNew);
  }, [activeId, dataFull, segmentValue]);

  /** 搜索关键词 - undefined 为默认值，不搜索，搜索后清空时值为空字符; 修复回退清空搜索时没有还原列表的 bug */
  const [search, setSearch] = useState<string | undefined>(undefined);
  const location = useLocation();
  const shareBack = location?.query?.id || '';

  const lazyScrollRef = useRef<HTMLDivElement>(null);

  const isInsightsReport = reportType === 'insightsReports';
  const [tmpSearch, setTmpSearch] = useState('')
  // insights reports 需要根据排序选项排序，其他 reportType 数据无特殊处理
  const sortedDataPartial = useMemo(() => {
    if(search && tmpSearch !== search) {
      setTmpSearch(search);
      return dataPartial
    };
    if (!isInsightsReport) return dataPartial;
    return sortReportsData(dataPartial, orderType);
  }, [dataPartial, orderType, isInsightsReport]);

  const getReportMobileList = async () => {
    setLoading(true);
    try {
      const res = await getReportFun?.();
      const tmp = [];
      const tmpSegment = [];
      const dataListNew = [];
      const dataListTop = [];
      let tmpArrNew = [];
      const tmpSegmentList = [{ id: 'all', label: 'All' }];
      if (Array.isArray(res?.resource_list) && res?.resource_list.length > 0) {
        res.resource_list.forEach((item, index) => {
          // const time = moment(item.update_time).format('YYYY-MM-DD');
          const time = moment(item.create_time).format('YYYY-MM-DD');
          const coverImg = item.cover?.replace(DATABRAIN_COS_DOMAIN, DATABRAIN_COS_ACCELERATE);
          const creator_head_img = item.creator_head_picture?.replace(
            DATABRAIN_COS_DOMAIN,
            DATABRAIN_COS_ACCELERATE,
          );
          const itemTag = splitReportTagStr(item?.tag);
          const itemTagEN = splitReportTagStr(item?.tag_en);
          const obj = {
            ...item,
            key: index,
            resource_id: item.resource_id,
            // coverImg: item.cover,
            coverImg,
            // 筛选时高亮会修改 title, 给选中文案添加标签样式
            // 避免 title 变化有影响使用，保留原有 title 信息到 originTitle
            title: [item.title, item.title_en],
            originTitle: [item.title, item.title_en],
            report_type: [item.report_type, item.report_type_en],
            summary: [item.introduction, item.introduction_en],
            tag: [itemTag, itemTagEN],
            tag_en: itemTagEN,
            time,
            source: item.source,
            authors: buildReportAuthors(item, { transformAvatar: transformAuthorAvatar }),
            report_like: item.report_like,
            update_time: item.update_time,
            create_time: item.create_time,
          };
          tmp.push(obj);
          if (tmpSegment.indexOf(item.source) == -1) {
            tmpSegment.push(item.source);
            tmpSegmentList.push({ id: item.source, label: item.source });
          }
        });
      }

      if (tmp && Array.isArray(tmp) && tmp?.length > 0) {
        const dataArr = tmp;
        // eslint-disable-next-line no-restricted-syntax
        for (const n in dataArr) {
          // if (!!moment(dataArr[n].update_time).isAfter(moment().subtract(1, 'month'))) {
          if (!!moment(dataArr[n].create_time).isAfter(moment().subtract(1, 'month'))) {
            dataListNew.push(dataArr[n]);
          } else {
            dataListTop.push(dataArr[n]);
          }
        }
        // dataListNew.sort((a, b) => moment(b.update_time) - moment(a.update_time));
        dataListNew.sort(
          (a, b) => moment(b.create_time).valueOf() - moment(a.create_time).valueOf(),
        );
        tmpArrNew = [...dataListNew, ...dataListTop];
      } else {
        tmpArrNew = [];
      }

      // console.log(tmp)
      // console.log(tmpSegmentList)
      //  setSegmentList(tmpSegmentList)
      setDataFull(tmpArrNew);
      setDataShow(tmpArrNew);
      setDataPartial(tmpArrNew);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      console.log(e);
    }
  };

  const onSearch = (word) => {
    if (cacheRecentSearch.indexOf(word) == -1 && word != '') {
      const c = [...cacheRecentSearch];
      c.push(word);
      setCacheRecentSearch(c);
      localStorage.setItem(searchType, JSON.stringify(c));
    }
    onSearchRes(word);
    if (pageLogMes?.pageId) {
      buriedPointSearch({ extInfo: pageLogMes?.pageId, typeId: pageLogMes?.typeId }, word);
    }
  };

  const searchThrottleFun = debounce(onSearch, 600);

  useEffect(() => {
    if (typeof search === 'string') {
      searchThrottleFun(search);
    }
  }, [search]);
  const rules: any = {
    other: 1000, // 匹配数量
    title: 1000,
    tag: 800,
    summary: 600,
    report_like: 600,
    report_type: 600,
    creator_name: 400,
    creator_department: 200,
    create_time: 1,
  };
  const onSearchRes = (word: string) => {
    let newData: any = [];
    if (typeof word === 'string' && word === '') {
      newData = dataShow;
    } else {
      dataShow.forEach((item: any, idx) => {
        let obj: any;
        item.orderNum = 0;
        const orderArr = [
          'summary',
          'creator_department',
          'tag',
          'creator_name',
          'title',
          'report_like',
          'report_type',
          'time',
        ];
        orderArr.map((orderItem: string) => {
          if (
            orderItem === 'summary' ||
            orderItem === 'creator_name' ||
            orderItem === 'creator_department' ||
            orderItem === 'title' ||
            orderItem === 'report_type' ||
            orderItem === 'time'
          ) {
            let tdata = item[orderItem][lang];
            if (orderItem === 'time') tdata = item[orderItem];
            if (tdata.toUpperCase().indexOf(word.toUpperCase()) != -1) {
              const itemUpper = tdata.toUpperCase();
              const replaceWord = tdata.substr(itemUpper.indexOf(word.toUpperCase()), word.length);
              const replaceHtml = `<span style="color:#326ff0">${replaceWord}</span>`;
              const regExp = new RegExp(replaceWord, 'gi');
              const newTdata = tdata.replace(regExp, replaceHtml);

              const matches = tdata.match(regExp);
              const orderNum = (item.orderNum =
                item.orderNum + rules[orderItem] + matches.length * 1000);
              if (orderItem !== 'time') {
                let tDataList = [];
                if (lang == 0) {
                  tDataList = [newTdata, item[orderItem][1]];
                } else {
                  tDataList = [item[orderItem][0], newTdata];
                }
                if (obj) {
                  obj = { ...obj, [orderItem]: tDataList, orderNum };
                } else {
                  obj = { ...item, [orderItem]: tDataList, key: idx, orderNum };
                }
              } else {
                if (obj) {
                  obj = { ...obj, [orderItem]: newTdata, orderNum };
                } else {
                  obj = { ...item, [orderItem]: newTdata, key: idx, orderNum };
                }
              }
            }
          } else if (orderItem === 'tag') {
            const tag = item.tag[lang];
            if (tag.toString().toUpperCase().indexOf(word.toUpperCase()) != -1) {
              const newTag = tag.map((s: string) => {
                const itemUpper = s.toUpperCase();
                if (itemUpper.indexOf(word.toUpperCase()) === -1) return s;
                const replaceWord = s.substr(itemUpper.indexOf(word.toUpperCase()), word.length);
                const replaceHtml = `<span style="color:#326ff0">${replaceWord}</span>`;
                const regExp = new RegExp(replaceWord, 'gi');
                const newS = s.replace(regExp, replaceHtml);
                const matches = s.match(regExp);
                item.orderNum += matches?.length * 1000;
                return newS;
              });
              const orderNum = (item.orderNum += rules.tag);
              let taLgList = [];
              if (lang == 0) {
                taLgList = [newTag, item.tag[1]];
              } else {
                taLgList = [item.tag[0], newTag];
              }
              if (obj) {
                obj = { ...obj, tag: taLgList, orderNum };
              } else {
                obj = { ...item, tag: taLgList, key: idx, orderNum };
              }
            }
          }
          // else if (orderItem === "report_like") {
          //   const orderNum = item.orderNum = item.orderNum + rules[orderItem] + item[orderItem];
          //   if (obj) {
          //     obj = { ...obj, orderNum: orderNum };
          //   }
          // }
        });
        if (obj) {
          newData.push(obj);
        }
      });
    }
    newData = newData.sort((a: any, b: any) => {
      if (b.orderNum === a.orderNum) {
        return new Date(b.time).valueOf() - new Date(a.time).valueOf();
      }
      return b.orderNum - a.orderNum;
    });
    setDataPartial(JSON.parse(JSON.stringify(newData)));
  };

  const getRecentSearchList = () => {
    try {
      const searchRecent = localStorage.getItem(searchType) || '[]';
      const searchRecentList = JSON.parse(searchRecent);
      const list = Array.isArray(searchRecentList) ? searchRecentList : [];
      // console.log("1", list)
      setCacheRecentSearch(list);
    } catch (error) {
      console.log(error);
    }
  };

  const onClear = () => {
    onSearchRes('');
  };

  const handleSearchRecentClick = (item) => {
    setSearch(item);
    // onSearchRes(item)
  };

  const deleteSearchRecent = (e, index, item) => {
    // console.log('delete', item)
    const c = arrayRemoveElement(cacheRecentSearch, item);

    setCacheRecentSearch(c);
    localStorage.setItem(searchType, JSON.stringify(c));
  };

  function getLoadMoreList(nextId: number | undefined, limit: number): Promise<Result> {
    let start = 0;
    if (nextId) {
      start = sortedDataPartial.findIndex((i) => i.key === nextId);
    }
    const end = start + limit;
    const list = sortedDataPartial.slice(start, end);
    const nId = sortedDataPartial[end] ? sortedDataPartial[end].key : undefined;

    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          list,
          nextId: nId,
        });
      }, 500);
    });
  }

  const { data, loading, loadingMore, noMore, mutate } = useInfiniteScroll(
    (d) =>
      // console.log(d)
      getLoadMoreList(d?.nextId, 6),
    {
      target: lazyScrollRef,
      isNoMore: (d) => d?.nextId === undefined,
      reloadDeps: [sortedDataPartial],
    },
  );

  /**
   * @description: 获得当前展示语言
   * 文案语言始终跟随系统 locale，不受语言 Tab 影响；
   * 语言 Tab 仅用于过滤报告以及决定 PDF 文件的语言版本。
   */
  const lang = useMemo(() => {
    return locale === LANGUAGE_CN ? 0 : 1;
  }, [locale]);

  const handleTabContent = (activeId: string) => {
    setSearch('');
    if (activeId === 'all') {
      setDataShow(dataFull);
      setDataPartial(dataFull);
    } else {
      const tmpData = dataFull?.filter((item) => {
        if (typeof item?.source === 'string') {
          return item?.source?.toLocaleUpperCase() == activeId?.toLocaleUpperCase();
        }
        return false;
      });
      setDataShow(tmpData);
      setDataPartial(tmpData);
    }
  };

  useEffect(() => {
    getRecentSearchList();
    getReportMobileList();
  }, []);

  return {
    segmentList,
    activeId,
    setActiveId,
    handleTabContent,
    onSearch,
    handleSearchRecentClick,
    deleteSearchRecent,
    cacheRecentSearch,
    dataLoading,
    dataPartial,
    lang,
    search,
    onClear,
    shareBack,
    lazyScrollRef,
    data,
    loading,
    loadingMore,
    noMore,
    mutate,
    orderType,
    setOrderType,
    languageSegmentList,
    segmentValue,
    onSegmentChange: handleSegmentChange,
  };
};
